import { useEffect } from 'react';
import { breakpoints } from '@/themes/themeOverrides/Breakpoints';

const SCROLL_LOCK_CONTAINER_SELECTOR = '.wrapper, .RaLayout-content, #main-content, .scroll-container, .RaDatagrid-tableWrapper';
const PAGINATION_SELECTOR = '#main-content .MuiTablePagination-root';
const PAGINATION_LOCK_CLASS = 'pagination-scroll-locked';

let lockCount = 0;

const getScrollbarCompensation = () => {
    const measured = window.innerWidth - document.documentElement.clientWidth;
    if (measured > 0) {
        return measured;
    }

    // MUI ModalManager often hides the scrollbar first and stores the gap as body padding.
    return Number.parseFloat(document.body.style.paddingRight) || 0;
};

const isInsideScrollableOverlay = target =>
    target?.closest?.(
        '.MuiModal-root:not(.MuiModal-hidden) .MuiPaper-root, .MuiPopover-root:not([aria-hidden="true"]) .MuiPaper-root, .MuiAutocomplete-popper',
    );

const preventBackgroundScroll = event => {
    if (isInsideScrollableOverlay(event.target)) {
        return;
    }

    event.preventDefault();
};

const pinPaginationInPlace = () => {
    if (window.innerWidth < breakpoints.values.lg) {
        return;
    }

    const pagination = document.querySelector(PAGINATION_SELECTOR);
    if (!pagination) {
        return;
    }

    const { top, left, width } = pagination.getBoundingClientRect();
    const rootStyle = document.documentElement.style;
    rootStyle.setProperty('--pagination-lock-top', `${top}px`);
    rootStyle.setProperty('--pagination-lock-left', `${left}px`);
    rootStyle.setProperty('--pagination-lock-width', `${width}px`);
    pagination.classList.add(PAGINATION_LOCK_CLASS);
};

const unpinPagination = () => {
    document.querySelectorAll(`.${PAGINATION_LOCK_CLASS}`).forEach(element => {
        element.classList.remove(PAGINATION_LOCK_CLASS);
    });

    const rootStyle = document.documentElement.style;
    rootStyle.removeProperty('--pagination-lock-top');
    rootStyle.removeProperty('--pagination-lock-left');
    rootStyle.removeProperty('--pagination-lock-width');
};

const applyLock = () => {
    const scrollY = window.scrollY;
    const bodyHeight = document.body.scrollHeight;
    const scrollbarWidth = getScrollbarCompensation();

    // Capture viewport position before sticky breaks under position:fixed.
    pinPaginationInPlace();

    document.documentElement.dataset.scrollY = scrollY.toString();
    document.documentElement.style.top = `-${scrollY}px`;
    document.documentElement.style.setProperty('--scrollbar-compensation', `${scrollbarWidth}px`);
    document.body.style.height = `${bodyHeight}px`;

    document.documentElement.classList.add('noscroll');
    document.body.classList.add('noscroll');
    document.querySelectorAll(SCROLL_LOCK_CONTAINER_SELECTOR).forEach(element => {
        if (!isInsideScrollableOverlay(element)) {
            element.classList.add('noscroll');
        }
    });
    // Take over from MUI after our inset is applied, so close cannot drop the gap.
    document.documentElement.dataset.scrollLockBodyPaddingRight = document.body.style.paddingRight;
    document.body.style.paddingRight = '0px';
    document.addEventListener('wheel', preventBackgroundScroll, { passive: false });
    document.addEventListener('touchmove', preventBackgroundScroll, { passive: false });
};

const removeLock = () => {
    const scrollY = document.documentElement.dataset.scrollY ? Number.parseInt(document.documentElement.dataset.scrollY, 10) : 0;

    document.removeEventListener('wheel', preventBackgroundScroll);
    document.removeEventListener('touchmove', preventBackgroundScroll);

    document.documentElement.classList.remove('noscroll');
    document.body.classList.remove('noscroll');
    document.querySelectorAll(SCROLL_LOCK_CONTAINER_SELECTOR).forEach(element => {
        element.classList.remove('noscroll');
    });
    unpinPagination();

    document.documentElement.style.top = '';
    document.body.style.height = '';
    document.body.style.paddingRight = '';
    document.documentElement.style.removeProperty('--scrollbar-compensation');

    delete document.documentElement.dataset.scrollY;

    if (scrollY) {
        window.scrollTo(0, scrollY);
    }
};

/**
 * Locks background scroll. Supports multiple concurrent consumers via ref counting.
 */
export const useScrollLock = active => {
    useEffect(() => {
        if (!active) {
            return undefined;
        }

        lockCount += 1;
        if (lockCount === 1) {
            applyLock();
        }

        return () => {
            lockCount -= 1;
            if (lockCount === 0) {
                removeLock();
            }
        };
    }, [active]);
};
